/* Grid of earned kiwi skins grouped by archetype — popup reveals variants. */

import { useState } from "react";
import { KiwiSkinRenderer } from "./KiwiSkinRenderer";
import { RARITY_COLORS } from "./kiwiRarityColors";
import type { KiwiSkinConfig, VariantArchetype, Rarity, PaletteName } from "@/types/kiwiSkin";
import { cn } from "@/lib/utils";

// ── Constants ────────────────────────────────────────────────────────

const ALL_VARIANTS: VariantArchetype[] = [
  "astronaut", "pirate", "ninja", "wizard", "robot", "ghost",
  "chef", "angel", "devil", "nerd", "knight", "dj",
  "scholar", "reader", "scientist", "brainy",
];

const VARIANT_LABELS: Record<VariantArchetype, string> = {
  astronaut: "Astronaut",
  pirate: "Pirate",
  ninja: "Ninja",
  wizard: "Wizard",
  robot: "Robot",
  ghost: "Ghost",
  chef: "Chef",
  angel: "Angel",
  devil: "Devil",
  nerd: "Nerd",
  knight: "Knight",
  dj: "DJ",
  scholar: "Scholar",
  reader: "Reader",
  scientist: "Scientist",
  brainy: "Brainy",
};

const SIGNATURE_PALETTES: Record<VariantArchetype, PaletteName> = {
  astronaut: "ice",
  pirate: "retro",
  ninja: "night",
  wizard: "gold",
  robot: "neon",
  ghost: "lavender",
  chef: "ember",
  angel: "cherry",
  devil: "cherry",
  nerd: "bubblegum",
  knight: "storm",
  dj: "neon",
  scholar: "gold",
  reader: "retro",
  scientist: "mint",
  brainy: "lavender",
};

const RARITY_RANK: Record<Rarity, number> = {
  Legendary: 0, Epic: 1, Rare: 2, Uncommon: 3, Common: 4,
};

// ── Types ────────────────────────────────────────────────────────────

interface KiwiSkinWithMeta extends KiwiSkinConfig {
  _id: string;
  earnedAt: number;
}

interface KiwiCollectionGridProps {
  skins: KiwiSkinWithMeta[];
  onSelect?: (skinId: string) => void;
  activeKiwiSkinId?: string;
}

// ── Helpers ──────────────────────────────────────────────────────────

function groupByVariant(skins: KiwiSkinWithMeta[]): Map<VariantArchetype, KiwiSkinWithMeta[]> {
  const map = new Map<VariantArchetype, KiwiSkinWithMeta[]>();
  for (const v of ALL_VARIANTS) map.set(v, []);
  for (const skin of skins) {
    map.get(skin.variant)?.push(skin);
  }
  for (const [, group] of map) {
    group.sort((a, b) => RARITY_RANK[a.rarity] - RARITY_RANK[b.rarity]);
  }
  return map;
}

function bestRarity(skins: KiwiSkinWithMeta[]): Rarity | null {
  if (skins.length === 0) return null;
  return skins.reduce(
    (best, s) => (RARITY_RANK[s.rarity] < RARITY_RANK[best] ? s.rarity : best),
    skins[0].rarity,
  );
}

// ── Sub-components ───────────────────────────────────────────────────

function VariantHeroIcon({ variant, size = 48 }: { variant: VariantArchetype; size?: number }) {
  const config: KiwiSkinConfig = {
    name: "",
    variant,
    palette: SIGNATURE_PALETTES[variant],
    environment: "none",
    artifact: "none",
    effect: "none",
    rarity: "Common",
  };
  return <KiwiSkinRenderer config={config} style={{ width: size, height: size }} />;
}

interface ArchetypeCardProps {
  variant: VariantArchetype;
  count: number;
  isSelected: boolean;
  bestRarityColor: string;
  onClick: () => void;
}

function ArchetypeCard({ variant, count, isSelected, bestRarityColor, onClick }: ArchetypeCardProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex flex-col items-center gap-1.5 py-3 px-1.5 rounded-xl border-2 transition-all",
        isSelected
          ? "border-primary bg-primary/10"
          : "border-border/40 bg-muted/30 hover:bg-muted/50",
        count === 0 && "opacity-35 cursor-default",
      )}
      style={isSelected ? { borderColor: bestRarityColor, background: `${bestRarityColor}10` } : undefined}
    >
      <VariantHeroIcon variant={variant} size={48} />
      <span className="text-[11px] font-bold leading-tight text-center">
        {VARIANT_LABELS[variant]}
        {count > 0 && (
          <span className="opacity-50 font-medium"> ({count})</span>
        )}
      </span>
    </button>
  );
}

interface VariantPopupProps {
  variant: VariantArchetype;
  skins: KiwiSkinWithMeta[];
  onSelect?: (skinId: string) => void;
  activeKiwiSkinId?: string;
}

function VariantPopup({ variant, skins, onSelect, activeKiwiSkinId }: VariantPopupProps) {
  return (
    <div
      className="absolute top-[calc(100%+10px)] left-1/2 -translate-x-1/2 z-10 w-[408px] p-3 rounded-xl border shadow-xl"
      style={{
        background: "rgba(30,30,40,0.95)",
        borderColor: "rgba(255,255,255,0.12)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2)",
        backdropFilter: "blur(12px)",
      }}
    >
      {/* Arrow */}
      <div
        className="absolute -top-1.5 left-1/2 -translate-x-1/2 rotate-45 size-3"
        style={{
          background: "rgba(30,30,40,0.95)",
          borderTop: "1px solid rgba(255,255,255,0.12)",
          borderLeft: "1px solid rgba(255,255,255,0.12)",
        }}
      />

      {/* Header */}
      <div className="mb-2.5">
        <div className="text-[13px] font-bold text-white">{VARIANT_LABELS[variant]}</div>
        <div className="text-[10px] text-white/50">{skins.length} variants</div>
      </div>

      {skins.length === 0 ? (
        <p className="text-[11px] text-white/50 text-center py-2">No variants yet</p>
      ) : (
        <div className="grid grid-cols-4 gap-2">
          {skins.map((skin) => {
            const rc = RARITY_COLORS[skin.rarity] ?? "#999";
            const isActive = activeKiwiSkinId === skin._id;
            return (
              <button
                key={skin._id}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect?.(skin._id);
                }}
                className={cn(
                  "flex flex-col items-center gap-1 py-2.5 px-2 rounded-lg border transition-all",
                  isActive && "ring-2 ring-white/40",
                  onSelect && "cursor-pointer hover:brightness-110",
                )}
                style={{
                  background: `${rc}15`,
                  borderColor: `${rc}30`,
                }}
              >
                <KiwiSkinRenderer config={skin} className="size-[52px]" />
                <span className="text-[10px] font-semibold text-center leading-tight text-white">
                  {skin.name}
                </span>
                <span
                  className="text-[8px] font-bold uppercase tracking-wider px-1 py-0 rounded-full"
                  style={{ color: "#fff", backgroundColor: rc }}
                >
                  {skin.rarity}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────

export function KiwiCollectionGrid({ skins, onSelect, activeKiwiSkinId }: KiwiCollectionGridProps) {
  const [selected, setSelected] = useState<VariantArchetype | null>(null);
  const grouped = groupByVariant(skins);

  if (skins.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        Complete all your daily tag goals to earn kiwi skins!
      </p>
    );
  }

  // Only show archetypes that have at least one skin
  const populatedVariants = ALL_VARIANTS.filter((v) => (grouped.get(v)?.length ?? 0) > 0);

  const popSelectedIdx = selected ? populatedVariants.indexOf(selected) : -1;
  const hasPrev = popSelectedIdx > 0;
  const hasNext = popSelectedIdx >= 0 && popSelectedIdx < populatedVariants.length - 1;

  return (
    <div>
      {/* Nav buttons row */}
      <div className="flex items-center justify-end gap-1 mb-2">
        <button
          onClick={() => { if (hasPrev) setSelected(populatedVariants[popSelectedIdx - 1]); }}
          disabled={!hasPrev}
          className={cn(
            "size-7 rounded-lg border border-border/50 flex items-center justify-center text-sm",
            hasPrev ? "opacity-70 hover:opacity-100 hover:bg-muted/50 cursor-pointer" : "opacity-20 cursor-default",
          )}
        >
          ‹
        </button>
        <button
          onClick={() => { if (hasNext) setSelected(populatedVariants[popSelectedIdx + 1]); }}
          disabled={!hasNext}
          className={cn(
            "size-7 rounded-lg border border-border/50 flex items-center justify-center text-sm",
            hasNext ? "opacity-70 hover:opacity-100 hover:bg-muted/50 cursor-pointer" : "opacity-20 cursor-default",
          )}
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2.5">
      {populatedVariants.map((variant) => {
        const variantSkins = grouped.get(variant) ?? [];
        const count = variantSkins.length;
        const isSelected = selected === variant;
        const br = bestRarity(variantSkins);
        const rc = br ? RARITY_COLORS[br] : "rgba(128,128,128,0.3)";

        return (
          <div key={variant} className="relative">
            <ArchetypeCard
              variant={variant}
              count={count}
              isSelected={isSelected}
              bestRarityColor={rc}
              onClick={() => setSelected(isSelected ? null : variant)}
            />
            {isSelected && (
              <VariantPopup
                variant={variant}
                skins={variantSkins}
                onSelect={onSelect}
                activeKiwiSkinId={activeKiwiSkinId}
              />
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}

export { ALL_VARIANTS };
export type { KiwiSkinWithMeta, KiwiCollectionGridProps };
